import {
  type HighlightElementFragment,
  type HighlightHandle
} from './highlights-renderer.ts';

interface InternalFragment {
  readonly key: string;
  readonly role: HighlightElementFragment['role'];
  readonly element: HTMLElement;
}

interface BindingRegistration {
  readonly binding: (fragment: HighlightElementFragment) => void | (() => void);
  readonly boundKeys: Set<string>;
  readonly cleanups: Map<string, () => void>;
}

/**
 * Internal mutable bridge between a stable logical highlight and the DOM
 * fragments currently representing it. Rendering strategies replace the
 * fragment set; consumers only receive the read-only `HighlightHandle` API.
 */
export class MutableHighlightHandle implements HighlightHandle {
  readonly id: string;

  #fragments = new Map<string, InternalFragment>();
  #bindings = new Set<BindingRegistration>();
  #disposed = false;

  constructor(id: string) {
    this.id = id;
  }

  bindElements(
    binding: (fragment: HighlightElementFragment) => void | (() => void)
  ): () => void {
    if (this.#disposed) {
      return () => {};
    }

    const registration: BindingRegistration = {
      binding,
      boundKeys: new Set(),
      cleanups: new Map()
    };

    this.#bindings.add(registration);

    for (const fragment of this.#fragments.values()) {
      this.#bindFragment(registration, fragment);
    }

    return () => {
      if (!this.#bindings.delete(registration)) {
        return;
      }

      this.#cleanupRegistration(registration);
    };
  }

  setFragments(fragments: readonly InternalFragment[]) {
    if (this.#disposed) {
      return;
    }

    const nextFragments = new Map(fragments.map((fragment) => [fragment.key, fragment]));

    for (const [key, current] of this.#fragments) {
      const next = nextFragments.get(key);

      if (!next || next.element !== current.element || next.role !== current.role) {
        this.#cleanupFragment(key);
      }
    }

    this.#fragments = nextFragments;

    for (const fragment of fragments) {
      for (const registration of this.#bindings) {
        if (!registration.boundKeys.has(fragment.key)) {
          this.#bindFragment(registration, fragment);
        }
      }
    }
  }

  removeFragment(key: string) {
    if (this.#disposed || !this.#fragments.has(key)) {
      return;
    }

    this.#cleanupFragment(key);
    this.#fragments.delete(key);
  }

  dispose() {
    if (this.#disposed) {
      return;
    }

    this.#disposed = true;

    for (const registration of this.#bindings) {
      this.#cleanupRegistration(registration);
    }

    this.#bindings.clear();
    this.#fragments.clear();
  }

  #bindFragment(registration: BindingRegistration, fragment: InternalFragment) {
    registration.boundKeys.add(fragment.key);
    const cleanup = registration.binding({
      role: fragment.role,
      element: fragment.element
    });

    if (cleanup) {
      registration.cleanups.set(fragment.key, cleanup);
    }
  }

  #cleanupFragment(key: string) {
    for (const registration of this.#bindings) {
      registration.cleanups.get(key)?.();
      registration.boundKeys.delete(key);
      registration.cleanups.delete(key);
    }
  }

  #cleanupRegistration(registration: BindingRegistration) {
    for (const cleanup of registration.cleanups.values()) {
      cleanup();
    }

    registration.cleanups.clear();
    registration.boundKeys.clear();
  }
}
